import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { CdkDrag, CdkDragEnd } from '@angular/cdk/drag-drop';
import { TranslatePipe } from '@ngx-translate/core';
import { AdaIntegrationFacade } from '@core/ada/facades/ada-integration.facade';
import { AdaFacade } from '@core/ada/facades/ada.facade';
import { AdaTokenFacade } from '@core/ada/facades/ada-token.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Permission } from '@kaseya-shared';
import { SpinnerComponent, TooltipDirective } from '@kaseya-design-components';
import { AuthorizationService } from '@core/authorization/services/authorization.service';

@Component({
  selector: 'itc-ada-chat-button',
  templateUrl: 'ada-chat-button.component.html',
  styleUrl: 'ada-chat-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, TooltipDirective, CdkDrag, SpinnerComponent],
})
export class AdaChatButtonComponent {
  private readonly adaIntegrationFacade = inject(AdaIntegrationFacade);
  private readonly adaFacade = inject(AdaFacade);
  private readonly adaTokenFacade = inject(AdaTokenFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isAdaEmbedLoaded = this.adaFacade.isAdaEmbedLoaded;
  private readonly initialButtonPosition = { x: 0, y: 0 };
  private readonly hasAdaModify = this.permissionsFacade.hasPermission(Permission.AdaModify);

  private hasBeenDragged = false;

  readonly isChatOpened = this.adaFacade.isChatOpened;
  readonly hasUnreadMessages = this.adaFacade.hasUnreadMessages;
  readonly isTokenLoading = this.adaTokenFacade.isTokenLoading;
  readonly isDragging = signal(false);
  readonly dragPosition = signal(this.initialButtonPosition);
  readonly isButtonDisplayed = computed(
    () => !this.isChatOpened() && this.isAdaEmbedLoaded() && this.hasAdaModify() && !this.adaIntegrationFacade.isChatHidden()
  );

  constructor() {
    this.resetButtonPositionOnLogout();
  }

  handleDragStart(): void {
    this.isDragging.set(true);
  }

  handleDragEnd({ source }: CdkDragEnd): void {
    const { x, y } = source.getFreeDragPosition();

    this.isDragging.set(false);
    this.dragPosition.set({ x, y });
    this.hasBeenDragged = true;
  }

  openAdaChat(): void {
    if (this.hasBeenDragged) {
      this.hasBeenDragged = false;
      return;
    }

    if (this.isChatOpened()) {
      return;
    }

    this.adaIntegrationFacade.openAdaChat();
  }

  private resetButtonPositionOnLogout(): void {
    this.authorizationService.onLogout
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dragPosition.set(this.initialButtonPosition));
  }
}
